import { optionIndex, parseNumericAnswer } from "./exam.js";
import type { Diagram, UasaQuestion } from "./uasaPaper.js";

/**
 * Online practice for a UASA paper: every question becomes one or more A/B/C(/D) items, built on the client from
 * the paper's own answer scheme (nothing new is generated). Long worded answers cannot be a tick-box: those become
 * optional typed items, and the page tells the pupil to print the paper instead.
 */
export interface PracticeItem {
  key: string;
  ref: string;
  marks: number;
  /** Index into paper.questions, for pictures that belong to the stem. */
  q: number;
  stem: string;
  passage?: string;
  wordBank?: string[];
  diagram?: Diagram;
  options: string[];
  optionImageIds?: (string | null)[];
  /** Index of the right option; -1 for typed items. */
  answer: number;
  typed?: boolean;
  model?: string;
  tf?: boolean;
}

/** Answer-scheme answers sometimes carry the mark tag, e.g. "Garis selari. (1 markah)". */
export const cleanAnswer = (a: string) => a.replace(/\s*\(\s*\d+\s*markah\s*\)\.?\s*$/i, "").trim();
const LONG = (s: string) => s.length > 42 || s.trim().split(/\s+/).length > 7;

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function rng(seed: string) {
  let a = hash(seed) || 1;
  return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function shuffle<T>(xs: T[], seed: string): T[] {
  const r = rng(seed), a = [...xs];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; }
  return a;
}
const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

const NUM = /\d[\d ]*(?:[.,]\d+)?/;
/** Wrong-but-plausible variants of a numeric answer, keeping its unit/prefix. */
function numericDistractors(answer: string, seed: string): string[] {
  const frac = /^(\d+)\/(\d+)$/.exec(answer.trim());
  if (frac) {
    const n = +frac[1]!, d = +frac[2]!;
    return [`${d}/${n}`, `${n + 1}/${d}`, `${Math.max(1, n - 1)}/${d}`, `${n}/${d + 1}`, `${n}/${d * 2}`].filter((x) => x !== answer.trim());
  }
  const m = NUM.exec(answer);
  const v = parseNumericAnswer(answer);
  if (!m || v === null) return [];
  const tok = m[0]!.trimEnd();
  const dec = /[.,](\d+)$/.exec(tok)?.[1]?.length ?? 0;
  const grouped = / /.test(tok);
  const fmt = (x: number) => {
    let s = Math.abs(x).toFixed(dec);
    if (grouped) { const [i, f] = s.split("."); s = i!.replace(/\B(?=(\d{3})+(?!\d))/g, " ") + (f ? `.${f}` : ""); }
    return answer.replace(tok, s).trim();
  };
  const step = dec ? Math.pow(10, -dec) : 1;
  const cand = [v + step, v - step, v + 10 * step, v - 10 * step, v * 2, v / 2, v + 2 * step, v - 2 * step, v + 100 * step, v * 10, v + 5 * step, v - 5 * step]
    .map((x) => (dec ? Math.round(x * 10 ** dec) / 10 ** dec : Math.round(x)))
    .filter((x) => x >= 0 && x !== v && Number.isFinite(x));
  // digit swap, a very common slip
  const digits = String(Math.abs(Math.round(v))).split("");
  if (digits.length >= 2) { const sw = [...digits]; [sw[0], sw[1]] = [sw[1]!, sw[0]!]; const sv = Number(sw.join("")); if (sv !== Math.round(v)) cand.splice(2, 0, sv); }
  const out: string[] = [];
  for (const x of shuffle(cand, seed + "n")) { const s = fmt(x); if (!out.some((o) => norm(o) === norm(s)) && norm(s) !== norm(answer)) out.push(s); if (out.length >= 5) break; }
  return out;
}

const UNITS = ["satu", "dua", "tiga", "empat", "lima", "enam", "tujuh", "lapan", "sembilan"];
const SCALE = ["ratus", "ribu", "juta", "puluh"];
const NUMW = new Set([...UNITS, "sepuluh", "sebelas", "belas", "puluh", "ratus", "ribu", "juta", "seratus", "seribu", "sejuta", "dan", "kosong", "sifar"]);
/** "Lima puluh lapan ribu tiga ratus enam belas." – a number written in Malay words. */
const isNumberWords = (a: string) => { const w = a.toLowerCase().replace(/[.,]/g, "").split(/\s+/).filter(Boolean); return w.length >= 2 && w.every((x) => NUMW.has(x)); };
function numberWordDistractors(answer: string, seed: string): string[] {
  const dot = /[.]$/.test(answer.trim()) ? "." : "";
  const w = answer.trim().replace(/[.]$/, "").split(/\s+/);
  const out = new Set<string>();
  const r = rng(seed + "w");
  const idx = w.map((x, i) => (UNITS.includes(x.toLowerCase()) ? i : -1)).filter((i) => i >= 0);
  for (let t = 0; t < 40 && out.size < 4; t++) {
    const m = [...w];
    const mode = Math.floor(r() * 3);
    if (mode < 2 && idx.length) {
      const i = idx[Math.floor(r() * idx.length)]!;
      const alt = UNITS.filter((u) => u !== m[i]!.toLowerCase());
      const pick = alt[Math.floor(r() * alt.length)]!;
      m[i] = i === 0 ? pick[0]!.toUpperCase() + pick.slice(1) : pick;
    } else {
      const si = m.map((x, i) => (SCALE.includes(x.toLowerCase()) ? i : -1)).filter((i) => i >= 0);
      if (si.length < 2) continue;
      const a = si[0]!, b = si[si.length - 1]!;
      [m[a], m[b]] = [m[b]!, m[a]!];
    }
    const o = m.join(" ") + dot;
    if (o.toLowerCase() !== answer.toLowerCase()) out.add(o);
  }
  return [...out];
}

/** Build choices: correct answer + up to 3 distractors from `pool` (same family first), placed deterministically. */
function choose(answer: string, pool: string[], seed: string): { options: string[]; answer: number } | null {
  const numeric = parseNumericAnswer(answer) !== null || /^\d+\/\d+$/.test(answer.trim());
  let d = numeric ? numericDistractors(answer, seed) : isNumberWords(answer) ? numberWordDistractors(answer, seed) : [];
  const same = pool.filter((p) => norm(p) !== norm(answer) && !LONG(p) && (parseNumericAnswer(p) !== null) === numeric);
  d = [...d, ...shuffle(same, seed + "p")];
  const uniq: string[] = [];
  for (const x of d) if (norm(x) !== norm(answer) && !uniq.some((u) => norm(u) === norm(x))) uniq.push(x);
  if (uniq.length < 2) return null;
  const opts = shuffle([answer, ...uniq.slice(0, 3)], seed + "o");
  return { options: opts, answer: opts.findIndex((o) => norm(o) === norm(answer)) };
}

/** Items whose wrong options need a language model (worded answers); the API asks once per paper and caches the reply. */
export interface DistractorAsk { key: string; stem: string; answer: string }
export function buildPractice(questions: UasaQuestion[], ai: Record<string, string[]> = {}, ask?: DistractorAsk[]): PracticeItem[] {
  // Short answers across the paper feed each other's distractors.
  const pool: string[] = [];
  for (const q of questions) {
    if (q.kind !== "objektif" && q.answer && !LONG(cleanAnswer(q.answer))) pool.push(cleanAnswer(q.answer));
    for (const p of q.parts ?? []) if (p.answer && !LONG(cleanAnswer(p.answer))) pool.push(cleanAnswer(p.answer));
    for (const b of q.blanks ?? []) if (b && !LONG(cleanAnswer(b))) pool.push(cleanAnswer(b));
  }
  const items: PracticeItem[] = [];
  const typed = (base: Omit<PracticeItem, "options" | "answer" | "typed" | "model">, model: string) => items.push({ ...base, options: [], answer: -1, typed: true, model });
  const gen = (base: Omit<PracticeItem, "options" | "answer">, raw: string | undefined, extra: string[] = []) => {
    if (!raw) return;
    const answer = cleanAnswer(raw);
    if (!answer) return;
    if (LONG(answer) && !isNumberWords(answer)) return typed(base, answer);
    const numeric = parseNumericAnswer(answer) !== null || /^\d+\/\d+$/.test(answer.trim());
    if (!numeric && !isNumberWords(answer) && extra.length < 2 && !ai[base.key]) ask?.push({ key: base.key, stem: `${base.passage ? base.passage + " " : ""}${base.stem}`, answer });
    const c = choose(answer, numeric ? [...extra, ...pool] : [...extra, ...(ai[base.key] ?? [])], base.key);
    if (c) items.push({ ...base, ...c }); else typed(base, answer);
  };

  questions.forEach((q, qi) => {
    const ref = `${q.section}${q.number}`;
    const key = (s = "") => `${ref}${s}`;
    const common = { q: qi, marks: q.marks, passage: q.passage, diagram: q.diagram };
    if (q.kind === "objektif") {
      const a = q.answer ? optionIndex(q.answer) : -1;
      if (q.options?.length && a >= 0) items.push({ ...common, key: key(), ref, stem: q.stemMs, options: q.options, optionImageIds: q.optionImageIds, answer: a });
      return;
    }
    if (q.kind === "padan" && q.pairs?.length) {
      const rights = q.pairs.map((p) => p.right.text).filter((x): x is string => !!x);
      q.pairs.forEach((p, i) => {
        if (!p.left.text || !p.right.text) return;
        const others = rights.filter((x) => x !== p.right.text);
        const opts = shuffle([p.right.text, ...shuffle(others, key(`p${i}`)).slice(0, 3)], key(`p${i}o`));
        if (opts.length >= 2) items.push({ ...common, key: key(`p${i}`), ref: `${ref}.${i + 1}`, marks: 1, stem: `${q.stemMs} — ${p.left.text}`, diagram: undefined, options: opts, answer: opts.indexOf(p.right.text) });
      });
      return;
    }
    if (q.kind === "tanda" && q.statements?.length) {
      q.statements.forEach((s, i) => { if (s.text) items.push({ ...common, key: key(`t${i}`), ref: `${ref}.${i + 1}`, marks: 1, stem: `${q.stemMs} — ${s.text}`, diagram: undefined, options: ["Betul", "Salah"], answer: s.correct ? 0 : 1, tf: true }); });
      return;
    }
    if (q.kind === "isian") {
      (q.blanks ?? []).forEach((b, i) => gen({ ...common, key: key(`b${i}`), ref: `${ref}(${i + 1})`, marks: 1, stem: q.stemMs, wordBank: q.wordBank }, b, q.wordBank ?? []));
      return;
    }
    if (q.kind === "susun" && q.answer && q.words?.length) {
      const ans = q.answer;
      const ws = ans.split(/\s+/);
      const alts = new Set<string>();
      const r = rng(key("s"));
      for (let t = 0; t < 30 && alts.size < 3 && ws.length > 1; t++) { const sw = [...ws]; const i = Math.floor(r() * ws.length); let j = Math.floor(r() * ws.length); if (i === j) j = (j + 1) % ws.length; [sw[i], sw[j]] = [sw[j]!, sw[i]!]; const s = sw.join(" "); if (s !== ans) alts.add(s); }
      const opts = shuffle([ans, ...alts], key("so"));
      if (opts.length >= 2) items.push({ ...common, key: key(), ref, stem: `${q.stemMs}: ${q.words.join(" / ")}`, options: opts, answer: opts.indexOf(ans) });
      else typed({ ...common, key: key(), ref, stem: q.stemMs }, ans);
      return;
    }
    if (q.parts?.length) {
      q.parts.forEach((p) => gen({ ...common, key: key(p.label), ref: `${ref}${p.label}`, marks: p.marks, stem: `${q.stemMs} ${p.stemMs}`.trim(), diagram: p.diagram ?? q.diagram }, p.answer));
      return;
    }
    gen({ ...common, key: key(), ref, stem: q.stemMs }, q.answer);
  });
  return items;
}
